import React, { useEffect, useState, useContext, useMemo } from "react";
import { BarChart } from '@mui/x-charts/BarChart';
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import { 
  StandardChartCard,
  useResponsiveChartProps,
  getXAxisConfig,
  getYAxisConfig,
  getImpactSeriesConfig
} from './common/ChartDefaults';
import { useMediaQuery, useTheme, Autocomplete, TextField, Box, CircularProgress, Typography } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';

const capitalizeSubject = (subject_group) => {
  if (!subject_group) return ''; // Handle null/undefined case
  return subject_group.charAt(0).toUpperCase() + subject_group.slice(1);
};

const SubjectsByImpactChart = ({
  showTitle = true,
  showSearch = true,
  // Optional props for dummy data
  businessData = null,
  reviewsDataOverride = null,
  viewOverride = null,
  title = "Impact by Subject"
}) => {
  // If props aren't provided, use real context:
  const businessContext = useContext(BusinessContext);
  const reviewsContext = useContext(ReviewsContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { chartSx, slotProps, margin } = useResponsiveChartProps();

  // Decide which data to use
  // Prioritize props if they exist
  const finalSelectedBusiness = businessData?.selectedBusiness !== undefined
    ? businessData.selectedBusiness
    : businessContext?.selectedBusiness;
  const finalCurrencySymbol = businessData?.currencySymbol !== undefined
    ? businessData.currencySymbol
    : businessContext?.currencySymbol || "$";
  // Get selectedBusinesses list for turnover lookup
  const selectedBusinesses = businessContext?.selectedBusinesses;

  // Use reviewsData and analysisData from context directly
  const {
    aggregatedData,
    isAggregatedLoading,
    subjectsChartData,
    isSubjectsChartLoading,
    fetchSubjectsChartData,
    view: contextView,
    isReviewsLoading,
    isAnalysisLoading,
    setSelectedSubject = () => {},
    setSelectedAttribute = () => {},
    setSelectedCategory = () => {},
    selectedSubject,
    selectedAttribute,
    selectedCategory,
    setSelectedDate = () => {},
    sortOption = "total",
    timeframe = "30"
  } = reviewsContext || {};

  // Get numSelected from business context
  const numSelected = businessContext?.selectedBusinesses?.length || 0;

  // console.log("[SubjectsChart] Received aggregatedData:", aggregatedData); // Log new aggregated data
  useEffect(() => {
    if (!isAggregatedLoading && aggregatedData) {
      // Removed console.log statements
    }
  }, [aggregatedData, isAggregatedLoading]);

  // Determine final data, prioritizing overrides if provided
  const finalView = viewOverride !== null ? viewOverride : (contextView || "Rating");

  const MAX_SUBJECTS = 8;

  // --- Calculate subject impact using useMemo --- 
  // THIS ENTIRE useMemo BLOCK IS REMOVED as data comes from backend
  /*
  const subjectImpactData = useMemo(() => {
    // ... original calculation ...
  }, [finalReviewsData, finalAnalysisData, selectedCategory, selectedBusinesses, numSelected, applySalesMultiplier]);
  */

  // NEW: Get subject impact data from aggregatedData context
  const subjectImpactDataFromBackend = useMemo(() => {
    if (isSubjectsChartLoading || !subjectsChartData) {
      return {};
    }
    // Transform backend data into the structure previously used by subjectImpactData memo
    // The backend already provides: originalCase, positiveRatingSum, negativeRatingSum, totalImpactSort, positiveRevenue, negativeRevenue, averagePositiveRating, averageNegativeRating
    const impactData = {};
    (subjectsChartData || []).forEach(item => {
      const lowerKeySubject = item.originalCase.toLowerCase();
      impactData[lowerKeySubject] = {
        ...item, // Spread all fields from backend item
      };
    });
    // console.log("[SubjectsChart] subjectImpactDataFromBackend result:", impactData);
    return impactData;
    // selectedCategory is a dependency because subjectsChartData from backend is filtered by it.
    // sortOption is NOT a dependency here, it's for the next memo.
  }, [subjectsChartData, isSubjectsChartLoading, selectedCategory]);

  // --- Calculate sorted subjects and top subjects using useMemo ---
  const { topSubjectsRaw, allSubjects } = useMemo(() => {
    // console.log("[SubjectsChart] Recalculating sorted subjects memo", sortOption);
    const impactData = subjectImpactDataFromBackend; // Use data from backend
    if (!impactData || Object.keys(impactData).length === 0) {
        return { topSubjectsRaw: [], allSubjects: [] };
    }

    const sortedByImpact = Object.keys(impactData).sort((a, b) => {
      switch (sortOption) {
        case 'positive':
          // Sort by summed positive rating
          return (impactData[b].positiveRatingSum || 0) - (impactData[a].positiveRatingSum || 0);
        case 'negative':
          // Sort by summed negative rating (most negative first)
          return (impactData[a].negativeRatingSum || 0) - (impactData[b].negativeRatingSum || 0);
        case 'total':
        default:
           // Sort by total absolute summed rating
          return (impactData[b].totalImpactSort || 0) - (impactData[a].totalImpactSort || 0);
      }
    });

    let topSubjectsLower;
    if (selectedCategory && selectedCategory !== "General" && selectedCategory !== "Experience") {
      topSubjectsLower = sortedByImpact; // Show all subjects for specific, non-General/Experience category
    } else {
      topSubjectsLower = sortedByImpact.slice(0, MAX_SUBJECTS); // Default behavior
    }

    const topSubjectsOriginalCase = topSubjectsLower.map(lower => impactData[lower].originalCase);

    let allSubs = sortedByImpact.map(lower => impactData[lower].originalCase);
    allSubs.sort((a, b) => a.localeCompare(b));

    // console.log("[SubjectsChart] sorted subjects result:", { topSubjectsRaw: topSubjectsOriginalCase, allSubjects: allSubs });
    return { topSubjectsRaw: topSubjectsOriginalCase, allSubjects: allSubs };
  }, [subjectImpactDataFromBackend, sortOption, MAX_SUBJECTS, selectedCategory]);

  // --- Calculate chart series data using useMemo ---
  const { positiveData, negativeData, chartSubjects } = useMemo(() => {
    const impactData = subjectImpactDataFromBackend;
    let finalChartSubjectsList;

    if (selectedSubject) {
      const isSelectedInTopRaw = topSubjectsRaw.some(
        (s) => s.toLowerCase() === selectedSubject.toLowerCase()
      );

      if (isSelectedInTopRaw) {
        // If selected subject is already in the top N, use top N as is.
        finalChartSubjectsList = [...topSubjectsRaw];
      } else {
        // Selected subject is not in top N.
        // Display all of topSubjectsRaw and append the selected subject.
        finalChartSubjectsList = [
          ...topSubjectsRaw, // All of the original top MAX_SUBJECTS
          selectedSubject,   // Append the selected subject
        ];
      }
    } else {
      // No subject selected via search, use the default top N.
      finalChartSubjectsList = [...topSubjectsRaw];
    }

    const posData = [];
    const negData = [];

    finalChartSubjectsList.forEach(subjectName => {
      // Ensure subjectName is a string before calling toLowerCase
      const lowerKey = typeof subjectName === 'string' ? subjectName.toLowerCase() : '';
      
      if (impactData[lowerKey]) { // Check if data exists for this subject key
        if (finalView === "Sales") {
          posData.push(impactData[lowerKey]?.positiveRevenue || 0);
          negData.push(impactData[lowerKey]?.negativeRevenue || 0);
        } else { // Rating View
          posData.push(impactData[lowerKey]?.averagePositiveRating || 0);
          negData.push(impactData[lowerKey]?.averageNegativeRating || 0);
        }
      } else {
        // Removed console.warn statement
        posData.push(0);
        negData.push(0);
      }
    });

    const result = {
      positiveData: posData,
      negativeData: negData,
      chartSubjects: finalChartSubjectsList
    };
    // console.log("[SubjectsChart] chart data result:", result);
    return result;
  }, [subjectImpactDataFromBackend, topSubjectsRaw, selectedSubject, finalView, MAX_SUBJECTS]);

  const onAxisClick = (event, params) => {
    event.stopPropagation();
    
    const clickedIndex = params?.dataIndex;
    if (clickedIndex !== undefined) {
      const clickedSubject = chartSubjects[clickedIndex];
      if (clickedSubject) {
        if (selectedSubject === clickedSubject) {
          setSelectedSubject(null);
          return;
        }

        setSelectedSubject(clickedSubject);
      }
    }
  };

  // Loading state for the chart
  const isLoading = isSubjectsChartLoading || (!subjectsChartData && (isReviewsLoading || isAnalysisLoading));

  // Early exit if no data or error states from context
  // Error state should be handled by ReviewsContext and shown at a higher level, or here if specific.
  if (isLoading) {
    return <StandardChartCard title={title} icon={CategoryIcon} showSearch={false} isLoading={true} />;
  }

  if (!subjectImpactDataFromBackend || Object.keys(subjectImpactDataFromBackend).length === 0) {
    // console.log("[SubjectsChart] No subject impact data to display.");
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
        <Typography variant="body2" color="textSecondary">No subject data found for the selected filters.</Typography>
      </Box>
    );
  }

  // Determine chart title based on active filters
  const getChartTitle = () => {
    if (!showTitle) return null;
    
    let chartTitle = title;
    const filters = [];
    
    if (selectedCategory) {
      filters.push(selectedCategory);
    }
    
    if (selectedAttribute) {
      filters.push(`"${selectedAttribute}"`);
    }
    
    if (filters.length > 0) {
      chartTitle = `${filters.join(' ')} Subjects`;
    }
    
    return chartTitle;
  };

  return (
    <StandardChartCard 
      data-chart-type="subject-chart"
      data-filter-exempt="true"
      title={getChartTitle()}
      titleIcon={<CategoryIcon />}
      action={showSearch && (
        <Autocomplete
          options={allSubjects}
          sx={{ 
            width: 120,
            ml: '10px',
            mr: '10px',
            mt: '10px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            }
          }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Search" 
              variant="outlined" 
              size="small"
              InputProps={{
                ...params.InputProps,
                style: { fontSize: '0.9rem' }
              }}
              InputLabelProps={{
                style: { fontSize: '0.9rem' }
              }}
            />
          )}
          ListboxProps={{
            style: { fontSize: '0.9rem' }
          }}
          slotProps={{
            paper: {
              style: { fontSize: '0.9rem' }
            },
          }}
          onChange={(e, newValue) => {
            if (e && e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation();
            }
            
            if (newValue === selectedSubject) {
              setSelectedSubject(null);
            } else {
              setSelectedSubject(newValue || null);
            }
          }}
          value={selectedSubject || null}
          clearOnEscape
        />
      )}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      ) : chartSubjects.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
          <Typography variant="body2" color="textSecondary">No subject data found for the selected filters.</Typography>
        </Box>
      ) : (
        <BarChart
          sx={chartSx}
          onAxisClick={onAxisClick}
          xAxis={[getXAxisConfig(chartSubjects, isSmallScreen, true)]}
          yAxis={[getYAxisConfig(finalCurrencySymbol, finalView, isSmallScreen)]}
          series={getImpactSeriesConfig(positiveData, negativeData, finalCurrencySymbol, finalView)}
          slotProps={{
            ...slotProps,
            root: {
              'data-chart-type': 'subject-chart',
              'data-filter-exempt': 'true'
            }
          }}
          margin={margin}
        />
      )}
    </StandardChartCard>
  );
};

export default SubjectsByImpactChart;
